import { formService } from '@/services/formService';

/**
 * Composable voor het tracken van product-configurator relaties
 * Gebruikt voor migration detection en updates
 */
export const useConfiguratorTracking = () => {
  /**
   * Vindt alle configurators die een specifiek product gebruiken
   */
  const getConfiguratorsUsingProduct = async (productId) => {
    try {
      // Haal alle forms op
      const forms = await formService.getForms();

      // Filter configurators die dit product gebruiken
      const configurators = forms.filter((form) => {
        if (form.type !== 'configurator') return false;

        const selectedProducts = form.settings?.cpq?.selectedProducts || [];
        // Extract product IDs (handles both string array and object array)
        const productIds = selectedProducts
          .map((item) => {
            if (typeof item === 'string') return item;
            return item.productId || item.id;
          })
          .filter(Boolean);
        return productIds.includes(productId);
      });

      return configurators;
    } catch (error) {
      console.error('Failed to get configurators using product:', error);
      return [];
    }
  };

  /**
   * Vindt alle configurators die meerdere producten gebruiken
   */
  const getConfiguratorsUsingProducts = async (productIds) => {
    try {
      const forms = await formService.getForms();

      const configurators = forms.filter((form) => {
        if (form.type !== 'configurator') return false;

        const selectedProducts = form.settings?.cpq?.selectedProducts || [];
        // Extract product IDs (handles both string array and object array)
        const productIdsFromSettings = selectedProducts
          .map((item) => {
            if (typeof item === 'string') return item;
            return item.productId || item.id;
          })
          .filter(Boolean);
        return productIds.some((productId) => productIdsFromSettings.includes(productId));
      });

      return configurators;
    } catch (error) {
      console.error('Failed to get configurators using products:', error);
      return [];
    }
  };

  /**
   * Update configurator na product wijziging
   */
  const updateConfiguratorAfterProductChange = async (
    configuratorId,
    productId,
    changeType = 'modified'
  ) => {
    try {
      const form = await formService.getFormById(configuratorId);

      if (changeType === 'deleted') {
        // Remove product from configurator
        const selectedProducts = form.settings?.cpq?.selectedProducts || [];
        // Filter out the product (handles both string array and object array)
        const updatedProducts = selectedProducts.filter((item) => {
          const id = typeof item === 'string' ? item : item.productId || item.id;
          return id !== productId;
        });

        // Remove product pages
        const updatedPages = form.pages.filter((page) => page.productId !== productId);

        const updatedForm = {
          ...form,
          pages: updatedPages,
          settings: {
            ...form.settings,
            cpq: {
              ...form.settings.cpq,
              selectedProducts: updatedProducts,
            },
          },
        };

        await formService.updateForm(configuratorId, updatedForm);
        return { success: true, action: 'product_removed' };
      }

      if (changeType === 'modified') {
        // Mark configurator as needing migration
        const updatedForm = {
          ...form,
          settings: {
            ...form.settings,
            migrationNeeded: {
              ...(form.settings.migrationNeeded || {}),
              [productId]: {
                detectedAt: new Date().toISOString(),
                changeType: 'modified',
              },
            },
          },
        };

        await formService.updateForm(configuratorId, updatedForm);
        return { success: true, action: 'migration_flagged' };
      }

      return { success: false, error: 'Unknown change type' };
    } catch (error) {
      console.error('Failed to update configurator after product change:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Batch update alle configurators na product wijziging
   */
  const notifyConfiguratorsOfProductChange = async (productId, changeType = 'modified') => {
    try {
      const configurators = await getConfiguratorsUsingProduct(productId);

      const updatePromises = configurators.map((configurator) =>
        updateConfiguratorAfterProductChange(configurator.id, productId, changeType)
      );

      const results = await Promise.all(updatePromises);

      return {
        success: true,
        affectedConfigurators: configurators.length,
        results,
      };
    } catch (error) {
      console.error('Failed to notify configurators of product change:', error);
      return {
        success: false,
        error: error.message,
        affectedConfigurators: 0,
      };
    }
  };

  /**
   * Get configurator usage statistics voor een product
   */
  const getProductUsageStats = async (productId) => {
    try {
      const configurators = await getConfiguratorsUsingProduct(productId);

      return {
        totalConfigurators: configurators.length,
        configuratorNames: configurators.map((c) => c.name),
        configuratorIds: configurators.map((c) => c.id),
        canSafelyDelete: configurators.length === 0,
      };
    } catch (error) {
      console.error('Failed to get product usage stats:', error);
      return {
        totalConfigurators: 0,
        configuratorNames: [],
        configuratorIds: [],
        canSafelyDelete: true,
      };
    }
  };

  /**
   * Check of configurators migration nodig hebben
   */
  const getConfiguratorsMigrationStatus = async () => {
    try {
      const forms = await formService.getForms();

      const configuratorsNeedingMigration = forms.filter((form) => {
        if (form.type !== 'configurator') return false;
        return (
          form.settings?.migrationNeeded && Object.keys(form.settings.migrationNeeded).length > 0
        );
      });

      return configuratorsNeedingMigration.map((configurator) => ({
        id: configurator.id,
        name: configurator.name,
        migrationNeeded: configurator.settings.migrationNeeded,
        affectedProducts: Object.keys(configurator.settings.migrationNeeded || {}),
      }));
    } catch (error) {
      console.error('Failed to get migration status:', error);
      return [];
    }
  };

  return {
    getConfiguratorsUsingProduct,
    getConfiguratorsUsingProducts,
    updateConfiguratorAfterProductChange,
    notifyConfiguratorsOfProductChange,
    getProductUsageStats,
    getConfiguratorsMigrationStatus,
  };
};
